import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, MapPin, Package, XCircle } from 'lucide-react';
import {
  Iskelet,
  Kart,
  KartBaslik,
  KartIcerik,
  Tablo,
  TabloBaslik,
  TabloBaslikHucresi,
  TabloGovde,
  TabloHucre,
  TabloSatir,
  TarihAraligiSecici,
  tarihIso,
  type TarihAraligi,
} from '@emanetx/ui';
import { analitikRaporuGetir } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

const PARA_SEMBOL: Record<string, string> = { TRY: '₺', EUR: '€', USD: '$' };

function son30Gun(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

function dkBicim(dk: number): string {
  if (dk < 60) return `${dk} dk`;
  const saat = Math.floor(dk / 60);
  const kalan = dk % 60;
  return kalan > 0 ? `${saat} sa ${kalan} dk` : `${saat} sa`;
}

export function SayfaAnalitik() {
  const [aralik, setAralik] = useState<TarihAraligi>(son30Gun);

  const sorgu =
    aralik.baslangic && aralik.bitis
      ? { baslangic: tarihIso(aralik.baslangic)!, bitis: tarihIso(aralik.bitis)! }
      : null;

  const rapor = useQuery({
    queryKey: ['raporlar', 'analitik', sorgu],
    queryFn: () => analitikRaporuGetir(istemci, sorgu!),
    enabled: !!sorgu,
  });

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Analitik</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            Kiralama hacmi, tamamlama oranı ve nokta performansı.
          </p>
        </div>
        <TarihAraligiSecici deger={aralik} onDegisim={setAralik} />
      </header>

      {rapor.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Iskelet className="h-28" />
          <Iskelet className="h-28" />
          <Iskelet className="h-28" />
          <Iskelet className="h-28" />
        </div>
      ) : !rapor.data ? null : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetrikKart
              ikon={Package}
              etiket="Toplam Kiralama"
              deger={rapor.data.toplamKiralama.toLocaleString('tr-TR')}
            />
            <MetrikKart
              ikon={Package}
              etiket="Tamamlanan"
              deger={rapor.data.tamamlanan.toLocaleString('tr-TR')}
              renk="text-emerald-600 dark:text-emerald-400"
              altMetin={
                rapor.data.toplamKiralama > 0
                  ? `%${((rapor.data.tamamlanan / rapor.data.toplamKiralama) * 100).toFixed(1)}`
                  : '—'
              }
            />
            <MetrikKart
              ikon={XCircle}
              etiket="İptal Edilen"
              deger={rapor.data.iptalEdilen.toLocaleString('tr-TR')}
              renk="text-red-600 dark:text-red-400"
              altMetin={
                rapor.data.toplamKiralama > 0
                  ? `%${((rapor.data.iptalEdilen / rapor.data.toplamKiralama) * 100).toFixed(1)}`
                  : '—'
              }
            />
            <MetrikKart
              ikon={Clock}
              etiket="Ortalama Süre"
              deger={dkBicim(rapor.data.ortalamaSureDk)}
            />
          </div>

          <Kart>
            <KartBaslik>
              <h3 className="flex items-center gap-2 text-base font-semibold">
                <MapPin size={16} className="text-marka-500" /> Nokta Performansı
              </h3>
            </KartBaslik>
            <KartIcerik>
              {rapor.data.noktaPerformans.length === 0 ? (
                <p className="text-fume-500 py-6 text-center text-sm">Veri yok.</p>
              ) : (
                <Tablo>
                  <TabloBaslik>
                    <tr>
                      <TabloBaslikHucresi>Kod</TabloBaslikHucresi>
                      <TabloBaslikHucresi>Ad</TabloBaslikHucresi>
                      <TabloBaslikHucresi>Kiralama</TabloBaslikHucresi>
                      <TabloBaslikHucresi>Gelir</TabloBaslikHucresi>
                    </tr>
                  </TabloBaslik>
                  <TabloGovde>
                    {rapor.data.noktaPerformans.map((n) => (
                      <TabloSatir key={n.noktaId}>
                        <TabloHucre>
                          <span className="text-marka-700 dark:text-marka-400 font-mono text-xs">
                            {n.noktaKod}
                          </span>
                        </TabloHucre>
                        <TabloHucre>
                          <span className="font-medium">{n.noktaAd}</span>
                        </TabloHucre>
                        <TabloHucre>
                          <span className="font-mono">{n.kiralamaSayisi}</span>
                        </TabloHucre>
                        <TabloHucre>
                          <span className="font-mono font-semibold">
                            {PARA_SEMBOL[n.paraBirimi]}
                            {Number(n.toplamGelir).toLocaleString('tr-TR', {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </TabloHucre>
                      </TabloSatir>
                    ))}
                  </TabloGovde>
                </Tablo>
              )}
            </KartIcerik>
          </Kart>
        </>
      )}
    </div>
  );
}

function MetrikKart({
  ikon: Ikon,
  etiket,
  deger,
  renk,
  altMetin,
}: {
  ikon: React.ComponentType<{ size?: number; className?: string }>;
  etiket: string;
  deger: string;
  renk?: string;
  altMetin?: string;
}) {
  return (
    <Kart>
      <KartIcerik className="space-y-1.5">
        <div className="text-fume-500 flex items-center gap-2 text-xs uppercase tracking-wider">
          <Ikon size={13} /> {etiket}
        </div>
        <div className={`font-mono text-2xl font-bold ${renk ?? ''}`}>{deger}</div>
        {altMetin && <div className="text-fume-400 text-xs">{altMetin}</div>}
      </KartIcerik>
    </Kart>
  );
}
